import React, { useState, useEffect, useRef } from 'react';
import Webcam from 'react-webcam';
import { X, Camera, Upload } from 'lucide-react';
import api, { getAssetUrl, getApiErrorMessage } from '../../services/api';
import { formatPersonName } from '../../utils/formatters';
import FacialAccessActions from '../../components/FacialAccessActions';

const INITIAL_FORM = {
  nome: '',
  cpf: '',
  unidade: '',
  categoria_unidade_id: '',
  contato: '',
  placa_carro: '',
  foto_url: null,
  status: true,
  grupo_ids: [],
};

const MoradorModal = ({ isOpen, onClose, moradorParaEditar }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [unidades, setUnidades] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [gruposError, setGruposError] = useState('');
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [unidadesError, setUnidadesError] = useState('');
  const [loadingUnidades, setLoadingUnidades] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoadingUnidades(true); setUnidadesError('');
    setLoadingGrupos(true); setGruposError('');
    api.get('/dispositivos/grupos').then(({ data }) => { if (active) setGrupos(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setGruposError('Falha ao carregar grupos de acesso. Tente abrir o cadastro novamente.'); })
      .finally(() => { if (active) setLoadingGrupos(false); });
    api.get('/unidades').then(({ data }) => { if (active) setUnidades(data); })
      .catch((error) => { if (active) setUnidadesError(getApiErrorMessage(error, 'Não foi possível carregar as unidades.')); })
      .finally(() => { if (active) setLoadingUnidades(false); });
    return () => { active = false; };
  }, [isOpen]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [imagemExpandida, setImagemExpandida] = useState(null);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const isSecureContextForCamera =
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Preenche o formulário ao editar ou limpa ao criar novo
  useEffect(() => {
    if (moradorParaEditar) {
      setFormData({
        ...INITIAL_FORM,
        ...moradorParaEditar,
        nome: moradorParaEditar.nome ?? '',
        cpf: moradorParaEditar.cpf ?? '',
        unidade: moradorParaEditar.unidade ?? '',
        categoria_unidade_id: moradorParaEditar.categoria_unidade_id ?? '',
        contato: moradorParaEditar.contato ?? '',
        placa_carro: moradorParaEditar.placa_carro ?? '',
        status: moradorParaEditar.status ?? true,
      });
      setPreviewUrl(getAssetUrl(moradorParaEditar.foto_url));
    } else {
      setFormData(INITIAL_FORM);
      setPreviewUrl(null);
    }
    setCameraError('');
    setSelectedFile(null);
    setCameraOpen(false);
    setVideoDevices([]);
    setSelectedDeviceId('');
    setImagemExpandida(null);
  }, [moradorParaEditar, isOpen]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const loadVideoDevices = React.useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setVideoDevices([]);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      setVideoDevices(cameras);
      setSelectedDeviceId((currentId) => {
        if (currentId && cameras.some((device) => device.deviceId === currentId)) {
          return currentId;
        }
        return cameras[0]?.deviceId || '';
      });
    } catch {
      setVideoDevices([]);
    }
  }, []);

  const handleCameraError = React.useCallback((error) => {
    const errorName = error?.name || '';

    if (!isSecureContextForCamera) {
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
      setCameraError('O navegador bloqueou a camera. Libere a permissao da camera no cadeado do site.');
      return;
    }

    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      setCameraError('Nenhuma camera foi encontrada neste computador.');
      return;
    }

    if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
      setCameraError('A camera esta ocupada por outro programa, como Zoom, Meet ou Teams.');
      return;
    }

    setCameraError('Nao foi possivel abrir a camera neste navegador. Envie uma imagem do computador.');
  }, [isSecureContextForCamera]);

  const handleOpenCamera = React.useCallback(async () => {
    setSelectedFile(null);
    setCameraError('');

    if (!isSecureContextForCamera) {
      setCameraOpen(false);
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    await loadVideoDevices();
    setCameraOpen(true);
  }, [isSecureContextForCamera, loadVideoDevices]);

  const handleCloseCamera = React.useCallback(() => {
    setCameraOpen(false);
    setCameraError('');
  }, []);

  const handleUserMedia = React.useCallback(() => {
    setCameraError('');
    loadVideoDevices();
  }, [loadVideoDevices]);

  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setCameraError('Nao foi possivel capturar a imagem da camera.');
      return;
    }

    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'morador.jpg', { type: 'image/jpeg' });
        setSelectedFile(file);
        setCameraOpen(false);
        setCameraError('');
      })
      .catch(() => {
        setCameraError('Nao foi possivel capturar a imagem da camera.');
      });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCameraOpen(false);
    setSelectedFile(file);
    setCameraError('');
  };

  const abrirImagem = (src, alt) => {
    if (src) {
      setImagemExpandida({ src, alt });
    }
  };

  // MÁSCARA DE CPF: 000.000.000-00
  const handleCPFChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove tudo que não é número
    if (value.length > 11) value = value.slice(0, 11); // Limita a 11 dígitos

    // Aplica a formatação visual
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");

    setFormData({ ...formData, cpf: value });
  };

  // MÁSCARA DE TELEFONE: (00) 00000-0000
  const handleContatoChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);

    if (value.length > 10) {
      value = value.replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    } else {
      value = value.replace(/^(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }

    setFormData({ ...formData, contato: value });
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (e.target !== e.currentTarget) return;
    const cpf = String(formData.cpf ?? '').replace(/\D/g, '');
    if (cpf.length !== 11) {
      alert('Informe um CPF com 11 dígitos para salvar os dados do morador.');
      return;
    }
    if (loadingGrupos || gruposError) { alert('Aguarde o carregamento dos grupos de acesso antes de salvar.'); return; }
    const dadosParaEnviar = new FormData();
    dadosParaEnviar.append('nome', formatPersonName(formData.nome));
    dadosParaEnviar.append('cpf', cpf);
    dadosParaEnviar.append('unidade', formData.unidade);
    dadosParaEnviar.append('categoria_unidade_id', formData.categoria_unidade_id || '');
    dadosParaEnviar.append('contato', (formData.contato || '').replace(/\D/g, ""));
    dadosParaEnviar.append('placa_carro', (formData.placa_carro || '').trim().toUpperCase());
    dadosParaEnviar.append('status', String(formData.status ?? true));
    dadosParaEnviar.append('grupo_ids', JSON.stringify(formData.grupo_ids || []));
    if (selectedFile) {
      dadosParaEnviar.append('foto', selectedFile, selectedFile.name);
    }

    try {
      if (moradorParaEditar) {
        await api.put(`/moradores/${moradorParaEditar.id}`, dadosParaEnviar);
        alert('Dados atualizados com sucesso!');
      } else {
        await api.post('/moradores/', dadosParaEnviar);
        alert('Morador cadastrado com sucesso!');
      }
      onClose();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Erro ao processar a solicitação no servidor.');
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-4 md:px-5">
          <h2 className="text-xl font-bold text-slate-800">
            {moradorParaEditar ? 'Editar Morador' : 'Novo Morador'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nome Completo</label>
                <input 
                  type="text" 
                  required
                  value={formData.nome}
                  className="w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-condo-teal outline-none"
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  onBlur={() => setFormData((current) => ({ ...current, nome: formatPersonName(current.nome) }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">CPF</label>
                  <input 
                    type="text" 
                    required
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    className="w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-condo-teal outline-none"
                    onChange={handleCPFChange}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Unidade cadastrada</label>
                  <select aria-label="Unidade cadastrada" value={formData.categoria_unidade_id || ''} disabled={loadingUnidades || Boolean(unidadesError)} onChange={(event) => setFormData({ ...formData, categoria_unidade_id: event.target.value })} className="mb-3 w-full rounded-lg border border-gray-300 bg-white p-2.5 outline-none focus:ring-2 focus:ring-condo-teal">
                    <option value="">{loadingUnidades ? 'Carregando...' : 'Selecione uma unidade'}</option>
                    {unidades.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                  </select>
                  {unidadesError && <p role="alert" className="mb-3 text-sm text-red-600">{unidadesError}</p>}
                  {!loadingUnidades && !unidadesError && !unidades.length && <p className="mb-3 text-xs text-slate-500">Crie suas unidades pelo botão + em Configurações → Unidades.</p>}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Placa do Carro</label>
                  <input
                    type="text"
                    placeholder="ABC1D23"
                    value={formData.placa_carro || ''}
                    className="w-full rounded-lg border border-gray-300 p-2.5 uppercase focus:ring-2 focus:ring-condo-teal outline-none"
                    onChange={(e) => setFormData({ ...formData, placa_carro: e.target.value.toUpperCase() })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Unidade / Apartamento / Casa</label>
                  <input 
                    type="text" 
                    required
                    value={formData.unidade}
                    className="w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-condo-teal outline-none"
                    onChange={(e) => setFormData({...formData, unidade: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Contato (WhatsApp)</label>
                <input 
                  type="text" 
                  placeholder="(00) 00000-0000"
                  value={formData.contato}
                  className="w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-condo-teal outline-none"
                  onChange={handleContatoChange}
                />
              </div>
              <div>
                <label htmlFor="morador-grupo-acesso" className="mb-1 block text-sm font-medium text-gray-700">Grupo de acesso</label>
                <select id="morador-grupo-acesso" aria-label="Grupo de acesso" value={formData.grupo_ids?.[0] || ''} disabled={loadingGrupos || Boolean(gruposError)} onChange={event => setFormData(current => ({ ...current, grupo_ids: event.target.value ? [Number(event.target.value)] : [] }))} className="w-full rounded-lg border border-gray-300 bg-white p-2.5 outline-none focus:ring-2 focus:ring-condo-teal">
                  <option value="">{loadingGrupos ? 'Carregando...' : 'Selecione um grupo'}</option>
                  {grupos.map(group => <option key={group.id} value={group.id}>{group.nome}</option>)}
                </select>
                {gruposError && <p role="alert" className="mt-1 text-sm text-red-600">{gruposError}</p>}
                {!loadingGrupos && !gruposError && !grupos.length && <p className="mt-1 text-xs text-slate-500">Cadastre grupos em Dispositivos.</p>}
              </div>
            </div>

            <div className="space-y-3">

              <label className="block text-sm font-medium text-gray-700">Foto do Morador</label>
              <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="relative mx-auto flex min-h-[180px] max-w-sm items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white sm:min-h-[220px]">
                  {cameraOpen && !cameraError && isSecureContextForCamera ? (
                    <>
                      {videoDevices.length > 1 && (
                        <div className="absolute left-2 right-2 top-2 z-10">
                          <select
                            value={selectedDeviceId}
                            onChange={(e) => setSelectedDeviceId(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white/95 p-2 text-sm text-slate-700 shadow-sm outline-none"
                          >
                            {videoDevices.map((device, index) => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `Camera ${index + 1}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : { facingMode: 'user' }}
                        onUserMediaError={handleCameraError}
                        onUserMedia={handleUserMedia}
                        className="h-[180px] w-full object-contain sm:h-[220px]"
                      />
                      <div className="absolute bottom-2 right-2 flex gap-2">
                        <button
                          type="button"
                          onClick={handleCloseCamera}
                          className="rounded-full border border-slate-200 bg-white p-2 text-slate-950 shadow-sm transition hover:bg-slate-100"
                          style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                          aria-label="Fechar camera"
                        >
                          <X size={20} className="text-slate-950" />
                        </button>
                        <button
                          type="button"
                          onClick={capture}
                          className="rounded-full bg-slate-900 p-2 text-white"
                        >
                          <Camera size={20} />
                        </button>
                      </div>
                    </>
                  ) : previewUrl ? (
                    <button
                      type="button"
                      onClick={() => abrirImagem(previewUrl, 'Pré-visualização do morador')}
                      className="h-full w-full"
                      title="Ampliar foto"
                    >
                      <img
                        src={previewUrl}
                        alt="Pré-visualização do morador"
                        className="h-[180px] w-full object-contain sm:h-[220px]"
                      />
                    </button>
                  ) : (
                    <div className="flex h-[180px] flex-col items-center justify-center gap-3 px-4 text-center text-sm text-slate-600 sm:h-[220px]">
                      <Camera size={30} className="text-slate-400" />
                      <p>
                        {cameraError || 'Escolha se deseja abrir a camera ou enviar uma foto do computador.'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mx-auto mt-3 grid max-w-sm grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleOpenCamera}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Camera size={16} />
                    Abrir camera
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Upload size={16} />
                    Enviar foto
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
                {previewUrl && (
                  <button
                    type="button"
                    onClick={() => abrirImagem(previewUrl, 'Pré-visualização do morador')}
                    className="mt-3 flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5 text-left transition hover:bg-slate-50"
                  >
                    <img
                      src={previewUrl}
                      alt="Pré-visualização do morador"
                      className="h-16 w-16 rounded-lg border border-gray-200 bg-white object-contain"
                    />
                    <span className="text-sm text-slate-600">Pré-visualização da foto selecionada</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <button type="submit" className="mt-5 w-full rounded-lg bg-condo-dark py-3 font-bold text-white transition hover:bg-slate-700">
            {moradorParaEditar ? 'Salvar Alterações' : 'Cadastrar no Banco'}
          </button>

          <FacialAccessActions
            pessoa={moradorParaEditar ? {
              tipo: 'morador',
              id: moradorParaEditar.id,
              nome: moradorParaEditar.nome,
              foto_url: moradorParaEditar.foto_url,
              descricao: moradorParaEditar.unidade ? `Unidade ${moradorParaEditar.unidade}` : 'Morador',
            } : null}
          />
        </form>
      </div>
    </div>
      {imagemExpandida && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setImagemExpandida(null)}
        >
          <button
            type="button"
            onClick={() => setImagemExpandida(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
          >
            <X size={22} />
          </button>
          <img
            src={imagemExpandida.src}
            alt={imagemExpandida.alt}
            className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default MoradorModal;
